import { useUIColors } from '@/core/hooks';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useRef } from 'react';
import { Animated, DimensionValue, Platform, StyleSheet, View, ViewProps } from 'react-native';

export interface SkeletonProps extends ViewProps {
    width?: DimensionValue;
    height?: DimensionValue;
    borderRadius?: number;
    variant?: 'rectangular' | 'circular';
    baseColor?: string | undefined;
    highlightColor?: string | undefined;
}

/**
 * Base Skeleton component with shimmer animation
 * Used for loading states to show placeholder content
 * Features a moving gradient shimmer effect
 */
export const Skeleton: React.FC<SkeletonProps> = ({
    width = '100%',
    height = 20,
    borderRadius = 8,
    variant = 'rectangular',
    baseColor: customBaseColor,
    highlightColor: customHighlightColor,
    style,
    ...props
}) => {
    const colors = useUIColors();
    const shimmerAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        // Create continuous shimmer animation loop
        // Slower animation for a more subtle, elegant effect
        const shimmerAnimation = Animated.loop(
            Animated.timing(shimmerAnim, {
                toValue: 1,
                duration: 2000, // Slower animation (2 seconds)
                useNativeDriver: Platform.OS !== 'web',
            })
        );

        shimmerAnimation.start();

        return () => {
            shimmerAnimation.stop();
        };
    }, [shimmerAnim]);

    // Calculate shimmer colors - use darker color for better visibility
    const baseColor = customBaseColor || colors.backgroundLevel2;
    const shimmerHighlight = customHighlightColor || colors.backgroundLevel4; // Darker color for better visibility
    
    const maxOpacity = 0.85;
    const isWeb = Platform.OS === 'web';
    
    // Move completely from left (-100%) to right (100%) relative to the container size
    const translateX = shimmerAnim.interpolate({
        inputRange: [0, 1],
        outputRange: isWeb ? ['-100%', '100%'] : [-300, 300], 
    });
    
    const shimmerOpacity = shimmerAnim.interpolate({
        inputRange: [0, 0.5, 1],
        outputRange: [isWeb ? 0.4 : 0, isWeb ? maxOpacity : 0.6, isWeb ? 0.4 : 0], 
    });

    const finalBorderRadius = variant === 'circular' 
        ? (typeof height === 'number' ? height / 2 : 999)
        : borderRadius;

    return (
        <View
            style={[
                styles.container,
                {
                    width,
                    height,
                    borderRadius: finalBorderRadius,
                    backgroundColor: baseColor,
                    ...(Platform.OS === 'web' ? { transform: [{ scale: 1 }] as any } : {}),
                },
                style,
            ]}
            {...props}
        >
            {/* Base background */}
            <View
                style={[
                    styles.baseBackground,
                    {
                        backgroundColor: baseColor,
                        borderRadius: finalBorderRadius,
                    },
                ]}
            />
            
            {/* Shimmer gradient overlay */}
            <Animated.View
                style={[
                    styles.shimmerContainer,
                    {
                        opacity: isWeb ? maxOpacity : shimmerOpacity,
                        transform: [{ translateX }],
                    },
                ]}
            >
                <LinearGradient
                    colors={isWeb ? [
                        'transparent',
                        shimmerHighlight,
                        'transparent',
                    ] : [
                        baseColor,
                        baseColor,
                        shimmerHighlight,
                        shimmerHighlight,
                        baseColor,
                        baseColor,
                    ]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={StyleSheet.absoluteFill}
                />
            </Animated.View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        overflow: 'hidden',
        position: 'relative',
    },
    baseBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    shimmerContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        width: Platform.OS === 'web' ? '100%' : '200%', 
    },
    shimmerGradient: {
        flex: 1,
        width: '100%',
        height: '100%',
    },
});

export default Skeleton;

